"use client";

import Image from "next/image";
import { useState, useRef, useEffect } from "react";

export function SdgTooltip({ goal }) {
    const [visible, setVisible] = useState(false);
    const timerRef = useRef(null);
    const wrapperRef = useRef(null);
    const [shiftX, setShiftX] = useState(0);

    // Ensure the tooltip does not bleed outside viewport edges on mobile
    useEffect(() => {
        if (visible && wrapperRef.current) {
            const wrapRect = wrapperRef.current.getBoundingClientRect();
            const center = wrapRect.left + wrapRect.width / 2;
            const tooltipWidth = 250;
            const margin = 16;
            let shift = 0;

            if (center - tooltipWidth / 2 < margin) {
                shift = margin - (center - tooltipWidth / 2);
            } else if (center + tooltipWidth / 2 > window.innerWidth - margin) {
                shift = (window.innerWidth - margin) - (center + tooltipWidth / 2);
            }
            setShiftX(shift);
        } else {
            setShiftX(0); // reset when closed
        }
    }, [visible]);

    // Close on click outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setVisible(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    function handleMouseEnter() {
        clearTimeout(timerRef.current);
        setVisible(true);
    }

    function handleMouseLeave() {
        timerRef.current = setTimeout(() => {
            setVisible(false);
        }, 150); // Small delay prevents flickering
    }

    function handleTap(e) {
        e.preventDefault();
        setVisible((prev) => !prev);
    }

    return (
        <div
            ref={wrapperRef}
            className="relative flex items-center justify-center"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleTap}
        >
            <Image
                src={goal.src}
                alt={goal.name}
                width={32}
                height={32}
                className="h-8 w-auto cursor-pointer transition-transform hover:scale-110"
            />

            {/* Tooltip isolation wrapper to prevent sdg-fade-in keyframe override from disrupting centering transforms */}
            {visible && (
                <div
                    className="absolute bottom-full left-1/2 z-50 mb-3 w-62.5 pointer-events-none"
                    style={{ transform: `translate(calc(-50% + ${shiftX}px), 0)` }}
                >
                    <div
                        className="rounded-xl bg-primary-dark p-3 text-center shadow-lg animate-in fade-in duration-200"
                        style={{ animation: "fade-up 0.15s ease-out forwards" }}
                    >
                        <h4 className="mb-1 text-xs font-semibold text-white">
                            {goal.name}
                        </h4>
                        <p className="text-[10px] text-zinc-200">{goal.description}</p>
                    </div>

                    {/* Down Arrow centering wrapper */}
                    <div
                        className="absolute left-1/2 top-full -mt-px pointer-events-none"
                        style={{ transform: `translateX(calc(-50% - ${shiftX}px))` }}
                    >
                        {/* Down Arrow visual */}
                        <div
                            className="border-[6px] border-transparent animate-in fade-in duration-200"
                            style={{
                                borderTopColor: "var(--primary-dark)",
                                animation: "fade-up 0.15s ease-out forwards",
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default function SdgTooltipList({ goals }) {
    return (
        <div className="flex flex-wrap items-center gap-2">
            {goals.map((goal) => (
                <SdgTooltip key={goal.name} goal={goal} />
            ))}
        </div>
    );
}
